import { useCallback } from 'react';
import { IconButton } from '@/layouts/authenticated/components/top-navigation/components/icon-button';
import { useCommandDialog } from '@/features/command-dialog';
import { useAnalytics } from '@/services/analytics';
import { KeyboardShortcut, InlineStack } from '@customerio/pluma-components/react';
import { useQueryParamAction } from '@/hooks/use-query-param-action';
import { useHotkey } from '@tanstack/react-hotkeys';

export function CommandDialogButton() {
  const { isOpen, open, close, getCloseTrackingSnapshot } = useCommandDialog();
  const { track } = useAnalytics();

  const handleShortcutClose = useCallback(() => {
    const { hadQuery, timeOpenMs } = getCloseTrackingSnapshot();
    void track('universalSearch', 'closed', {
      Reason: 'shortcut',
      'Had Query': hadQuery,
      'Time Open Ms': timeOpenMs,
    });
    close();
  }, [close, getCloseTrackingSnapshot, track]);

  const handleOpen = useCallback(
    (entryPoint: 'header' | 'shortcut' = 'header') => {
      if (isOpen) {
        return;
      }
      void track('universalSearch', 'opened', { 'Entry Point': entryPoint });
      open();
    },
    [isOpen, open, track],
  );

  useHotkey('Mod+K', () => {
    if (isOpen) {
      handleShortcutClose();
    } else {
      handleOpen('shortcut');
    }
  });

  // Deep-link support: ?cmd-search=<query> opens the dialog with a prefilled query
  // Note: uses 'cmd-search' instead of 'search' to avoid collision with deliverability page's ?search= param
  useQueryParamAction('cmd-search', (value) => {
    void track('universalSearch', 'opened', { 'Entry Point': 'deep_link' });
    open({ initialInputValue: value });
  });

  return (
    <IconButton
      icon="search"
      label="Search"
      tooltip={
        isOpen ? undefined : (
          <InlineStack gap="100" alignBlock="center">
            <span>Search</span>
            <KeyboardShortcut hotkey="Mod+K" isInverted />
          </InlineStack>
        )
      }
      onClick={() => {
        handleOpen();
      }}
      isDisabled={isOpen}
    />
  );
}
